import type { AdmissionsCitation } from './types';

export type CitationEntityKind = 'application' | 'family' | 'student' | 'resource' | 'reference';
export type CitationRouteTargetKind = Exclude<CitationEntityKind, 'reference'>;
export type CitationRouteTarget = {
  href: string;
  kind: CitationRouteTargetKind;
};

const UUID_LIKE_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ENTITY_CODE_PATTERN = /^(?:APP|FA|FAM|STU|RES|DOC)-[A-Z0-9]{2,}(?:-[A-Z0-9]{2,})*$/i;
const APPLICATION_CODE_PATTERN = /\b(?:APP|FA)-[A-Z0-9]{2,}(?:-[A-Z0-9]{2,})*\b/gi;
const GENERIC_ENTITY_LABEL_PATTERN = /^(?:application|family|student|resource|reference|focused application|priority queue application|the focused application|timeline event digest|calendar scheduling digest)$/i;

const toOptionalText = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const normalizeEntityLabel = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  const normalized = value
    .replace(/\[[^\]]+\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized || GENERIC_ENTITY_LABEL_PATTERN.test(normalized)) {
    return null;
  }
  return normalized.replace(/\s+family$/i, '').trim() || null;
};

const stripApplicationCodes = (value: string, applicationCode?: string | null): string => {
  let next = value.replace(APPLICATION_CODE_PATTERN, ' ');
  if (applicationCode) {
    const escapedCode = applicationCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    next = next.replace(new RegExp(escapedCode, 'ig'), ' ');
  }
  return next
    .replace(/[()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const deriveEntityLabelFromPreview = (citation: AdmissionsCitation): string | null => {
  const preview = toOptionalText(citation.preview);
  if (!preview) {
    return null;
  }
  const directFamilyMatch = preview.match(/^([A-Za-z][A-Za-z .,'-]{1,80})\s*\((?:APP|FA)-[A-Z0-9-]+\)$/i);
  if (directFamilyMatch?.[1]) {
    return normalizeEntityLabel(directFamilyMatch[1]);
  }
  const reverseFamilyMatch = preview.match(/^(?:APP|FA)-[A-Z0-9-]+\s*\(([A-Za-z][A-Za-z .,'-]{1,80})\)$/i);
  if (reverseFamilyMatch?.[1]) {
    return normalizeEntityLabel(reverseFamilyMatch[1]);
  }
  const familySuffixMatch = preview.match(/^([A-Za-z][A-Za-z .,'-]{1,80})\s+family$/i);
  if (familySuffixMatch?.[1]) {
    return normalizeEntityLabel(familySuffixMatch[1]);
  }
  if (/[.:]/.test(preview) || preview.split(/\s+/).length > 5) {
    return null;
  }
  return normalizeEntityLabel(stripApplicationCodes(preview, toOptionalText(citation.applicationCode)));
};

const normalizeModule = (citation: AdmissionsCitation): string => {
  const explicit = toOptionalText(citation.module)?.toLowerCase();
  if (explicit) {
    if (explicit === 'financialaid' || explicit === 'financial_aid') {
      return 'financial-aid';
    }
    return explicit;
  }
  const table = String(citation.table ?? '').toLowerCase();
  if (table.startsWith('financialaid')) {
    return 'financial-aid';
  }
  return 'admissions';
};

const isRouteEligibleApplicationId = (value: string | null): value is string => (
  typeof value === 'string' && UUID_LIKE_PATTERN.test(value)
);

export const parseCitationKey = (citation: AdmissionsCitation): Record<string, unknown> => {
  const key = citation.key;
  if (key && typeof key === 'object' && !Array.isArray(key)) {
    return key as Record<string, unknown>;
  }
  if (typeof key === 'string') {
    try {
      const parsed = JSON.parse(key);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
  }
  return {};
};

export const isIdentifierToken = (value: string): boolean => {
  const normalized = String(value ?? '').trim();
  if (!normalized) {
    return false;
  }
  return UUID_LIKE_PATTERN.test(normalized) || ENTITY_CODE_PATTERN.test(normalized);
};

export const inferEntityKindFromToken = (value: string): CitationEntityKind => {
  const normalized = String(value ?? '').trim().toUpperCase();
  if (!normalized) {
    return 'reference';
  }
  if (normalized.startsWith('APP-')) return 'application';
  if (normalized.startsWith('FA-')) return 'application';
  if (normalized.startsWith('FAM-')) return 'family';
  if (normalized.startsWith('STU-')) return 'student';
  if (normalized.startsWith('RES-') || normalized.startsWith('DOC-')) return 'resource';
  return UUID_LIKE_PATTERN.test(normalized) ? 'reference' : 'reference';
};

const inferEntityKindFromFieldPath = (fieldPath: string): CitationEntityKind => {
  const normalized = fieldPath.toLowerCase();
  if (normalized.includes('family')) return 'family';
  if (normalized.includes('student') || normalized.includes('child')) return 'student';
  if (normalized.includes('resource') || normalized.includes('document') || normalized.includes('file')) {
    return 'resource';
  }
  return 'application';
};

export const resolveCitationEntityKind = (citation: AdmissionsCitation): CitationEntityKind => {
  const entityCode = toOptionalText(citation.entityCode);
  if (entityCode) {
    const inferred = inferEntityKindFromToken(entityCode);
    if (inferred !== 'reference') {
      return inferred;
    }
  }
  return inferEntityKindFromFieldPath(String(citation.fieldPath ?? ''));
};

export const resolveCitationEntityLabel = (citation: AdmissionsCitation): string | null => {
  const explicit = normalizeEntityLabel(toOptionalText(citation.entityLabel));
  if (explicit) {
    return explicit;
  }
  return deriveEntityLabelFromPreview(citation);
};

export const resolveCitationApplicationId = (citation: AdmissionsCitation): string | null => {
  const key = parseCitationKey(citation);
  const table = String(citation.table ?? '').toLowerCase();
  const explicitApplicationId = toOptionalText(key.applicationId);
  if (isRouteEligibleApplicationId(explicitApplicationId)) {
    return explicitApplicationId;
  }
  if (table.includes('faq')) {
    return null;
  }
  const id = toOptionalText(key.id);
  if (!isRouteEligibleApplicationId(id)) {
    return null;
  }
  if (table.includes('application') || table.includes('financialaid')) {
    return id;
  }
  return null;
};

const buildAdmissionsHref = (applicationId: string, kind: CitationEntityKind): string => {
  const encodedId = encodeURIComponent(applicationId);
  if (kind === 'family') return `/school/admissions/family/${encodedId}`;
  if (kind === 'resource') return `/school/admissions/documents/${encodedId}`;
  return `/school/admissions/children/${encodedId}`;
};

const buildFinancialAidHref = (applicationId: string): string =>
  `/school/financialaid/staff/${encodeURIComponent(applicationId)}`;

const resolveCitationFamilyId = (citation: AdmissionsCitation): string | null => {
  const key = parseCitationKey(citation);
  const familyId = toOptionalText(key.familyId);
  return familyId ?? null;
};

const resolveCitationStudentId = (citation: AdmissionsCitation): string | null => {
  const key = parseCitationKey(citation);
  const studentId = toOptionalText(key.studentId);
  return studentId ?? null;
};

const buildStudentsFamiliesHref = (
  citation: AdmissionsCitation,
  kind: CitationEntityKind,
): string | null => {
  const familyId = resolveCitationFamilyId(citation);
  if (familyId) {
    const encodedFamilyId = encodeURIComponent(familyId);
    if (kind === 'resource') {
      return `/school/students/family/${encodedFamilyId}/documents`;
    }
    if (kind === 'family') {
      return `/school/students/family/${encodedFamilyId}/family`;
    }
    return `/school/students/family/${encodedFamilyId}/children`;
  }
  const studentId = resolveCitationStudentId(citation);
  if (!studentId) {
    return null;
  }
  return `/school/students/dashboard?studentId=${encodeURIComponent(studentId)}`;
};

export const resolveCitationHref = (
  citation: AdmissionsCitation,
  kindOverride?: CitationEntityKind
): string | null => {
  const module = normalizeModule(citation);
  const resolvedKind = kindOverride ?? resolveCitationEntityKind(citation);
  if (module === 'students-families') {
    return buildStudentsFamiliesHref(citation, resolvedKind);
  }
  const applicationId = resolveCitationApplicationId(citation);
  if (!applicationId) {
    return null;
  }
  if (resolvedKind === 'reference') {
    return null;
  }
  if (module === 'financial-aid') {
    return buildFinancialAidHref(applicationId);
  }
  if (module === 'admissions') {
    return buildAdmissionsHref(applicationId, resolvedKind);
  }
  return null;
};

export const resolveCitationRouteTarget = (citation: AdmissionsCitation): CitationRouteTarget | null => {
  const module = normalizeModule(citation);
  if (module === 'students-families') {
    const familyHref = resolveCitationHref(citation, 'family');
    if (familyHref) {
      return { href: familyHref, kind: 'family' };
    }
    const studentHref = resolveCitationHref(citation, 'student');
    if (studentHref) {
      return { href: studentHref, kind: 'student' };
    }
    const resourceHref = resolveCitationHref(citation, 'resource');
    if (resourceHref) {
      return { href: resourceHref, kind: 'resource' };
    }
  }

  const applicationHref = resolveCitationHref(citation, 'application');
  if (applicationHref) {
    return { href: applicationHref, kind: 'application' };
  }

  const familyHref = resolveCitationHref(citation, 'family');
  if (familyHref) {
    return { href: familyHref, kind: 'family' };
  }

  const studentHref = resolveCitationHref(citation, 'student');
  if (studentHref) {
    return { href: studentHref, kind: 'student' };
  }

  const resourceHref = resolveCitationHref(citation, 'resource');
  if (resourceHref) {
    return { href: resourceHref, kind: 'resource' };
  }

  return null;
};

export const resolveCitationRouteLabel = (
  citation: AdmissionsCitation,
  target: CitationRouteTarget
): string => {
  const entityLabel = resolveCitationEntityLabel(citation);
  if (entityLabel) {
    return entityLabel;
  }
  if (target.kind === 'application') {
    return 'Open application';
  }
  if (target.kind === 'family') {
    return 'Open family details';
  }
  if (target.kind === 'student') {
    return 'Open student details';
  }
  return 'Open resource';
};
